import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { workspaceDaysAgo } from '@/lib/workspace-display';

/**
 * ONE reporting window for the whole product.
 *
 * The complaint that produced this: "in the reports across so many pages, I am
 * unable to know what date range data I am looking at - again I have to scroll
 * to top to see and then come down". That is two problems wearing one coat.
 *
 * The window scrolled away, because every report drew its picker inside
 * PageHeader, which is page body. Fixed by rendering the control in the
 * Topbar, which is already sticky and had an unused slot - so it costs no
 * vertical space on any report.
 *
 * And the window kept CHANGING underneath the reader. Twenty-one report pages
 * each held their own `days`, seeded with six different defaults (7, 28, 30,
 * 90) - and DateWindow then forced any page opening above 7 back down to 7 on
 * mount, so the window silently reset on every navigation. A reader who set 90
 * days on Attribution and walked to Products was reading a week and had no
 * reason to suspect it. One window, held here, survives navigation.
 *
 * WHERE IT LIVES. The URL first, so back, bookmark and paste-to-a-colleague
 * all work, and localStorage behind it so a fresh navigation with no query
 * string resumes what the reader last chose rather than snapping to a default.
 * The URL wins on arrival; the store fills in when the URL is silent.
 *
 * WHAT A PAGE PROMISES. A page calls useReportWindow() to opt in. No call, no
 * control - Settings and Automations are not reports, and a window chip
 * hovering over them implies a filter that does not exist. A page that cannot
 * answer every window declares what it supports, and the chip then states the
 * window the DATA answered rather than the one that was asked for. Several
 * endpoints already clamp server-side (opportunities to 7-90 days, another to
 * 7-365); with a single global window those clamps become reachable, and an
 * unannounced clamp would put a "last year" label over ninety days of rows.
 */

const KEY = 'mm.report-window';
export const DEFAULT_DAYS = 28;

/*
 * WHERE "LAST N DAYS" ENDS (owner, 27 Oct 2026: "in meta when I click on last
 * 7 days, it takes the last 7 days excluding today, but in cookiebees ... it
 * includes today, which is not right as discrepancy will be there as today's
 * data is not resolved"). Meta and GA4 both mean the N complete days ending
 * yesterday. So does this window now, wherever the page can say so:
 *
 *   upto 'yesterday'  (the default) a report that takes exact dates reads the
 *                     N days ending yesterday, as a range (resolveWindow)
 *   upto 'today'      "Today", and "Last N days to today", chosen as such
 *
 * A page that answers "right now" (Orders, Event stream, Signal Health) says
 * `live`, and one whose server can only count back from today says
 * `trailingOnly`: both read the N days to today, and the picker says "to
 * today" over them rather than a label that would not be true.
 */
export const UPTO = { YESTERDAY: 'yesterday', TODAY: 'today' };

const Ctx = createContext(null);

const readStore = () => {
  try {
    const v = JSON.parse(localStorage.getItem(KEY) || 'null');
    // 'all' has to pass this too, or the window survives until the next reload
    // and then silently reverts to 28 days.
    if (v && (v.days === 'all' || Number(v.days) > 0 || (v.range?.from && v.range?.to))) return v;
  } catch { /* a blocked or corrupt store is not an error, it is a default */ }
  return null;
};

/**
 * The window a fresh load opens on, from the URL first and the store behind
 * it. A function of its own so the boot prefetch (lib/route-preload) asks the
 * server for exactly the window the page is about to ask for.
 */
export function initialWindow(params) {
  const raw = params.get('days');
  const qDays = Number(raw);
  const from = params.get('from'); const to = params.get('to');
  if (from && to) return { days: null, range: { from, to } };
  // A link shared from Orders can say ?days=all, so it has to arrive as that
  // and not as NaN. A page that cannot answer it falls back on its own.
  if (raw === 'all') return { days: 'all', range: null };
  if (qDays > 0) return { days: qDays, range: null, upto: params.get('upto') === UPTO.TODAY ? UPTO.TODAY : UPTO.YESTERDAY };
  return readStore() || { days: DEFAULT_DAYS, range: null, upto: UPTO.YESTERDAY };
}
export const windowWasChosen = (params) => Boolean(params.get('days') || params.get('from') || readStore());

// The window as it stands right now, for code outside React that has to name
// the same query a page will: a sidebar hover prefetching the report under the
// pointer. Written by the provider on every change; null before it mounts.
let live = null;
export const liveWindow = () => live;

export function ReportWindowProvider({ children }) {
  const [params, setParams] = useSearchParams();
  const { pathname } = useLocation();

  // The URL is the first authority on arrival: a pasted link must open on the
  // window it was sent with, whatever this browser last had stored.
  const [state, setState] = useState(() => initialWindow(params));

  // Pages register what they can answer. One at a time: two report pages are
  // never mounted together, and a stale registration would let a page that has
  // unmounted keep constraining the control.
  const [reg, setReg] = useState(null);

  // Non-null while a page is displaying its preferred opening: holds the
  // window that preference displaced, to be handed back when the page is left.
  const borrowed = useRef(null);

  // Did a HUMAN pick this window (or did one arrive in the URL / the store)?
  // Only an unchosen window may be replaced by a page's preferred opening.
  const chosen = useRef(windowWasChosen(params));
  // What the NEXT page will open on: a window borrowed by this page's
  // preferred opening is handed back when it is left.
  live = { ...(borrowed.current || state), chosen: chosen.current };

  // Persist what the reader chose, never what a page borrowed. A borrowed
  // window written to the store comes back on the next load looking exactly
  // like a choice, and from then on the whole product is stuck on the
  // operational page's week - the leak this borrowing was meant to prevent,
  // delayed by one reload rather than fixed.
  useEffect(() => {
    if (borrowed.current) return;
    try { localStorage.setItem(KEY, JSON.stringify(state)); } catch { /* private window */ }
  }, [state]);

  // Mirror into the URL. Only ever touches this window's own three params, so
  // a page's other filters ride along untouched, and always replaces rather
  // than pushes - a window change is not a place you want the back button to
  // walk through one preset at a time.
  const mirror = useRef(null);
  useEffect(() => {
    const want = state.range
      ? { from: state.range.from, to: state.range.to }
      : { days: String(state.days), ...(state.upto === UPTO.TODAY && state.days !== 'all' ? { upto: UPTO.TODAY } : {}) };
    // The path is part of the signature. React batches the outgoing page's
    // deregistration with the incoming page's registration into one commit, so
    // navigating between two reports on the same window produced an identical
    // signature and the write was skipped - the new route kept the window but
    // not the query string, and a link copied from it lost the window.
    const sig = pathname + JSON.stringify(want) + ':' + (reg ? '1' : '0');
    if (mirror.current === sig) return;
    mirror.current = sig;
    // Two guards, both learned from a blank home page (5 Sep 2026). A redirect
    // route (`/` -> /dashboard, /growth -> /dashboard) renders <Navigate> in
    // the same commit this effect runs in; setSearchParams navigates to the
    // pathname it captured, which is the OLD one, and wrote /app/growth back
    // over /app/dashboard - the reader saw an empty page at the old address.
    // (1) If the browser is no longer at this render's path, the router is
    // mid-redirect: do not write. (2) If the query would not change, do not
    // write at all - a no-op replace is still a navigation.
    const base = (document.querySelector('base')?.getAttribute('href') || '/app').replace(/\/$/, '');
    if (window.location.pathname.replace(/\/$/, '') !== (base + pathname).replace(/\/$/, '')) return;
    const next = new URLSearchParams(params);
    next.delete('days'); next.delete('from'); next.delete('to'); next.delete('upto');
    // Only a page that actually reports carries the window in its URL.
    if (reg) for (const [k, v] of Object.entries(want)) next.set(k, v);
    if (next.toString() === params.toString()) return;
    // Keep what the page was opened WITH. setSearchParams drops navigation
    // state, and this runs right after a link that carries some: Signal
    // catalogue's "Set it up" opened the workflow editor with its template
    // and this rewrite threw the template away, so the editor opened empty.
    setParams(next, { replace: true, state: window.history.state?.usr ?? undefined });
  }, [state, reg, pathname, params, setParams]);

  const setDays = useCallback((d, upto = UPTO.YESTERDAY) => {
    chosen.current = true;
    // 'all' is a window, not a count. Number('all') is NaN and NaN || 28 is 28,
    // so picking All time quietly selected the last 28 days instead - the
    // control moved, the preset appeared, and choosing it did nothing a reader
    // could see except change the label back.
    setState({ days: d === 'all' ? 'all' : (Number(d) || DEFAULT_DAYS), range: null, upto: upto === UPTO.TODAY ? UPTO.TODAY : UPTO.YESTERDAY });
  }, []);

  // A page's preferred opening, BORROWED rather than taken.
  //
  // Signal Health opening on a week is right; the whole product silently
  // becoming a week because Signal Health happened to be the first page
  // visited is the very fault this provider exists to remove. So a preference
  // remembers what it displaced and hands it back when the page is left -
  // unless a human chose a window in the meantime, in which case their choice
  // stands everywhere and there is nothing to give back.
  const adopt = useCallback((d) => {
    if (chosen.current) return;
    // 'all' is a window, not a number of days, so it must survive this
    // unchanged - Number('all') is NaN, and a page would then ask the API for
    // NaN days and get an empty list that looks like a store with no orders.
    const want = d === 'all' ? 'all' : Number(d);
    if (want !== 'all' && !Number.isFinite(want)) return;
    setState((s) => {
      if (s.range || s.days === want) return s;
      borrowed.current = s;
      return { days: want, range: null, upto: s.upto || UPTO.YESTERDAY };
    });
  }, []);
  const release = useCallback(() => {
    if (chosen.current || !borrowed.current) { borrowed.current = null; return; }
    const prev = borrowed.current;
    borrowed.current = null;
    setState(prev);
  }, []);
  const setRange = useCallback((r) => setState((s) => (chosen.current = true, r && r.from && r.to
    ? { days: null, range: { from: r.from, to: r.to } }
    : { days: s.days || DEFAULT_DAYS, range: null, upto: s.upto || UPTO.YESTERDAY })), []);

  const value = useMemo(() => ({ ...state, setDays, setRange, adopt, release, reg, setReg }),
    [state, setDays, setRange, adopt, release, reg]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

const useCtx = () => useContext(Ctx);

/**
 * A report page opts in.
 *
 *   supports: { min, max } - the narrowest and widest window this page's data
 *   can actually answer, in days. Omit when the page can answer anything.
 *
 * Returns the window plus `effectiveDays`: what the page should REQUEST after
 * its own limits are applied. A page must fetch with this, never with the raw
 * `days`, or it will ask for a window its endpoint silently clamps and then
 * label the answer with the question.
 */
export function useReportWindow(supports) {
  const ctx = useCtx();
  const min = supports?.min ?? null;
  const max = supports?.max ?? null;
  const prefer = supports?.prefer ?? null;
  // A page whose API measures "the last N days" only - no window that ended
  // in the past - says so, and the picker disables past-ending presets rather
  // than labelling a trailing answer with a historical question.
  const trailingOnly = supports?.trailingOnly ?? false;
  // A page about right now (Orders, Event stream, Signal Health): its window
  // runs to today whatever "Last N days" means on a report.
  const live = supports?.live ?? false;
  // A page that can answer "everything ever". Orders can - it is a list, and
  // the whole list is a sensible thing to ask for. A report with a comparison
  // period cannot: there is nothing before everything to compare against.
  //
  // It has to be declared per page because ONE window is shared across the
  // product. Without this, picking All time on Orders and then opening a report
  // that clamps to `min` days would hand it the string 'all' and compute
  // Math.max(7, 'all') - NaN, and a page asking the API for NaN days.
  const allowAll = supports?.allowAll ?? false;
  // A page that can compare against the period before lends the picker its
  // Compare switch: { on, set, note }.
  const compare = supports?.compare ?? null;
  const { setReg, adopt, release } = ctx || {};

  useEffect(() => {
    if (!setReg) return undefined;
    setReg({ min, max, trailingOnly, live, allowAll, compare });
    return () => setReg(null);
    // compare is a fresh object each render; register on its fields.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [min, max, trailingOnly, live, allowAll, compare?.on, compare?.note, setReg]);

  // A preferred opening, honoured ONLY while the reader has not chosen. Signal
  // Health and Event Health answer "is something broken right now", and 28 days
  // buries a failure that started yesterday - but the moment anyone picks a
  // window their choice wins everywhere, which is the whole point of holding
  // one window. A preference that could override a choice would reintroduce
  // exactly the reset this provider was built to remove.
  useEffect(() => {
    if (!prefer || !adopt) return undefined;
    adopt(prefer);
    return () => release && release();
  }, [prefer, adopt, release]);

  if (!ctx) {
    // Rendered outside the provider (a test, a standalone harness). Behave
    // like a page with its own window rather than throwing.
    return { days: DEFAULT_DAYS, range: null, effectiveDays: DEFAULT_DAYS, clamped: false,
      setDays: () => {}, setRange: () => {} };
  }

  return { ...resolveWindow(ctx, { min, max, allowAll, trailingOnly, live }), setDays: ctx.setDays, setRange: ctx.setRange };
}

/**
 * What a page with these limits requests for this window. Pure, so a prefetch
 * outside React computes the same `effectiveDays` (and so the same query key)
 * the page will.
 */
export function resolveWindow(ctx, { min = null, max = null, allowAll = false, trailingOnly = false, live = false, daysAgo = workspaceDaysAgo } = {}) {
  // 'all' reaching a page that never claimed it can answer it is the failure
  // this guards: the page reads its default instead, and says the window it
  // used rather than the one that was set elsewhere.
  const rawDays = ctx.days;
  const isAll = rawDays === 'all';
  const asked = ctx.range
    ? Math.max(1, Math.round((new Date(`${ctx.range.to}T00:00:00`) - new Date(`${ctx.range.from}T00:00:00`)) / 86400000) + 1)
    : (isAll && allowAll) ? 'all'
      : Number(rawDays) || DEFAULT_DAYS;
  if (asked === 'all') {
    return { days: 'all', range: null, effectiveDays: 'all', clamped: false, asked: 'all' };
  }
  let used = asked;
  if (min != null) used = Math.max(min, used);
  if (max != null) used = Math.min(max, used);

  // "Last N days" on a report that takes exact dates: the N complete days
  // ending yesterday, in the workspace's own calendar.
  const complete = !ctx.range && ctx.upto !== UPTO.TODAY && !trailingOnly && !live;
  const range = complete ? { from: daysAgo(used), to: daysAgo(1) } : ctx.range;
  return {
    days: (isAll && !allowAll) ? DEFAULT_DAYS : (ctx.days || DEFAULT_DAYS),
    range: range && range.from && range.to ? range : ctx.range,
    effectiveDays: used,
    clamped: used !== asked,
    asked,
    // The days run to today (live, trailing-only, or chosen to): the label says so.
    toToday: !ctx.range && !complete,
  };
}

/** The Topbar's view: the control appears only while a report is mounted. */
export function useWindowBar() {
  const ctx = useCtx();
  if (!ctx) return null;
  return ctx;
}
